import React, { useState, useEffect } from 'react';
import { getTransactions, getInventories, getItems, getCategories, getVendors, getDailyUsageReport, exportStockToExcel, exportTransactionsToExcel, exportReports, getSettings } from '../lib/api';
import { downloadBlob } from '../lib/downloadHelper';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Badge } from '../components/ui/badge';
import { 
  FileText, 
  Download, 
  Loader2, 
  Calendar,
  Activity,
  TrendingUp,
  Filter,
  ArrowUpDown,
  ChevronUp,
  ChevronDown,
  Printer
} from 'lucide-react';
import { LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from 'recharts';

const TOOLTIP_STYLE = { background: '#fff', border: '1px solid #E5E7EB', borderRadius: '8px', boxShadow: '0 4px 12px -2px rgba(0,0,0,0.1)' };
const DOT_STYLE = { fill: '#F0C500' };

const Reports = () => {
  const [inventories, setInventories] = useState([]);
  const [items, setItems] = useState([]);
  const [categories, setCategories] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [usageData, setUsageData] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [exportingTransactions, setExportingTransactions] = useState(false);
  
  // Filters
  const [selectedInventory, setSelectedInventory] = useState('all');
  const [selectedItem, setSelectedItem] = useState('all');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [selectedVendor, setSelectedVendor] = useState('all');
  const [selectedType, setSelectedType] = useState('all');
  const [dateRange, setDateRange] = useState('7');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  
  // Sorting
  const [sortBy, setSortBy] = useState('timestamp');
  const [sortOrder, setSortOrder] = useState('desc');
  
  // Show filters
  const [showFilters, setShowFilters] = useState(false);
  const [taxRate, setTaxRate] = useState(0);

  useEffect(() => {
    fetchInitialData();
    getSettings().then(res => setTaxRate(res.data.sales_tax_rate || 0)).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    fetchTransactions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedInventory, selectedItem, selectedCategory, selectedVendor, selectedType, startDate, endDate, sortBy, sortOrder]);

  useEffect(() => {
    fetchUsageData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedInventory, dateRange]);

  const fetchInitialData = async () => {
    try {
      const [invRes, itemsRes, catRes, vendorRes] = await Promise.all([
        getInventories(),
        getItems(),
        getCategories(),
        getVendors()
      ]);
      
      setInventories(invRes.data);
      setItems(itemsRes.data);
      setCategories(catRes.data);
      setVendors(vendorRes.data);
    } catch (error) {
      toast.error('Failed to load data');
    }
  };

  const fetchUsageData = async () => {
    try {
      const usageRes = await getDailyUsageReport({
        days: parseInt(dateRange),
        inventory_id: selectedInventory !== 'all' ? selectedInventory : undefined
      });
      setUsageData(usageRes.data);
    } catch (error) {
      toast.error('Failed to load usage data');
    }
  };

  const fetchTransactions = async () => {
    setLoading(true);
    try {
      const params = {
        limit: 200,
        sort_by: sortBy,
        sort_order: sortOrder
      };
      
      if (selectedInventory !== 'all') params.inventory_id = selectedInventory;
      if (selectedItem !== 'all') params.item_id = selectedItem;
      if (selectedCategory !== 'all') params.category_id = selectedCategory;
      if (selectedVendor !== 'all') params.vendor_id = selectedVendor;
      if (selectedType !== 'all') params.transaction_type = selectedType;
      if (startDate) params.start_date = startDate;
      if (endDate) params.end_date = endDate;
      
      const transRes = await getTransactions(params);
      setTransactions(transRes.data);
    } catch (error) {
      toast.error('Failed to load transactions');
    } finally {
      setLoading(false);
    }
  };

  const handleExportStock = async () => {
    setExporting(true);
    try {
      const response = await exportStockToExcel(selectedInventory !== 'all' ? selectedInventory : undefined);
      downloadBlob(response.data, `inventory_stock_${new Date().toISOString().split('T')[0]}.xlsx`);
    } catch (error) {
      alert('Export failed. Please try again.');
    } finally {
      setExporting(false);
    }
  };

  const handleExportTransactions = async () => {
    setExportingTransactions(true);
    try {
      const params = {};
      if (selectedInventory !== 'all') params.inventory_id = selectedInventory;
      if (selectedItem !== 'all') params.item_id = selectedItem;
      if (selectedCategory !== 'all') params.category_id = selectedCategory;
      if (selectedVendor !== 'all') params.vendor_id = selectedVendor;
      if (selectedType !== 'all') params.transaction_type = selectedType;
      if (startDate) params.start_date = startDate;
      if (endDate) params.end_date = endDate;
      
      const response = await exportReports(params);
      downloadBlob(response.data, `transaction_report_${new Date().toISOString().split('T')[0]}.xlsx`);
    } catch (error) {
      alert('Export failed. Please try again.');
    } finally {
      setExportingTransactions(false);
    }
  };

  const handlePrint = () => window.print();

  const handleSort = (column) => {
    if (sortBy === column) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(column);
      setSortOrder('desc');
    }
  };

  const getTransactionBadge = (type) => {
    const styles = {
      use: 'bg-[#E6F2FA] text-[#0776BD]',
      restock: 'bg-green-100 text-green-800',
      transfer_in: 'bg-purple-100 text-purple-800',
      transfer_out: 'bg-orange-100 text-orange-800',
      adjustment: 'bg-gray-100 text-gray-600'
    };
    return styles[type] || 'bg-gray-100 text-gray-600';
  };

  const SortIcon = ({ column }) => {
    if (sortBy !== column) return <ArrowUpDown size={14} className="text-gray-400" />;
    return sortOrder === 'asc' ? <ChevronUp size={14} /> : <ChevronDown size={14} />;
  };

  const totalUsageCount = usageData.reduce((sum, d) => sum + d.usage_count, 0);
  const totalUsageValue = usageData.reduce((sum, d) => sum + d.total_value, 0);
  const totalUsageTax = totalUsageValue * (taxRate / 100);

  // Transaction totals
  const restockTotal = transactions.filter(t => t.transaction_type === 'restock').reduce((sum, t) => sum + Math.abs(t.total_value), 0);
  const restockTax = restockTotal * (taxRate / 100);

  return (
    <div className="space-y-6 animate-slide-in" data-testid="reports-page">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="heading-2 text-[#1F2937]">Reports & Analytics</h1>
          <p className="text-[#6B7280]">Transaction history and usage analytics</p>
        </div>
        <div className="flex gap-2 print:hidden">
          <Button 
            onClick={handleExportStock} 
            disabled={exporting}
            className="gap-2 bg-[#0776BD] hover:bg-[#055A92] rounded-lg"
            data-testid="export-stock-btn"
          >
            {exporting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download size={18} />}
            Export Stock
          </Button>
          <Button 
            onClick={handleExportTransactions} 
            disabled={exportingTransactions}
            className="gap-2 bg-green-600 hover:bg-green-700 text-white rounded-lg"
            data-testid="export-transactions-btn"
          >
            {exportingTransactions ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download size={18} />}
            Export Report
          </Button>
          <Button
            onClick={handlePrint}
            variant="outline"
            className="gap-2 rounded-lg border-[#0776BD] text-[#0776BD]"
            data-testid="print-reports-btn"
          >
            <Printer size={18} />
            Print
          </Button>
        </div>
      </div>

      {/* Summary Cards */}
      <div className={`grid grid-cols-1 gap-4 ${taxRate > 0 ? 'md:grid-cols-4' : 'md:grid-cols-3'}`}>
        <Card className="bg-white rounded-xl border border-gray-200 shadow-soft">
          <CardContent className="p-6">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-[#E6F2FA] rounded-xl">
                <Activity size={24} className="text-[#0776BD]" />
              </div>
              <div>
                <p className="text-sm font-medium text-[#6B7280] uppercase tracking-wide">Total Uses ({dateRange}d)</p>
                <p className="text-3xl font-bold font-heading text-[#1F2937]">{totalUsageCount}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-white rounded-xl border border-gray-200 shadow-soft">
          <CardContent className="p-6">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-green-100 rounded-xl">
                <TrendingUp size={24} className="text-green-600" />
              </div>
              <div>
                <p className="text-sm font-medium text-[#6B7280] uppercase tracking-wide">{taxRate > 0 ? 'Usage Subtotal' : 'Usage Value'} ({dateRange}d)</p>
                <p className="text-3xl font-bold font-heading text-[#1F2937]">${totalUsageValue.toFixed(2)}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        {taxRate > 0 && (
          <Card className="bg-white rounded-xl border border-gray-200 shadow-soft">
            <CardContent className="p-6">
              <div className="flex items-center gap-4">
                <div className="p-3 bg-orange-100 rounded-xl">
                  <TrendingUp size={24} className="text-orange-600" />
                </div>
                <div>
                  <p className="text-sm font-medium text-[#6B7280] uppercase tracking-wide">After Tax ({taxRate}%)</p>
                  <p className="text-3xl font-bold font-heading text-[#1F2937]">${(totalUsageValue + totalUsageTax).toFixed(2)}</p>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        <Card className="bg-white rounded-xl border border-gray-200 shadow-soft">
          <CardContent className="p-6">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-purple-100 rounded-xl">
                <FileText size={24} className="text-purple-600" />
              </div>
              <div>
                <p className="text-sm font-medium text-[#6B7280] uppercase tracking-wide">Transactions Shown</p>
                <p className="text-3xl font-bold font-heading text-[#1F2937]">{transactions.length}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Usage Chart */}
      <Card className="bg-white rounded-xl border border-gray-200 shadow-soft">
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle className="font-heading text-lg text-[#1F2937]">Daily Usage Summary</CardTitle>
          <Select value={dateRange} onValueChange={setDateRange}>
            <SelectTrigger className="w-[150px] h-10 rounded-lg border-gray-300">
              <Calendar size={16} className="mr-2 text-[#6B7280]" />
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="7">Last 7 Days</SelectItem>
              <SelectItem value="14">Last 14 Days</SelectItem>
              <SelectItem value="30">Last 30 Days</SelectItem>
              <SelectItem value="90">Last 90 Days</SelectItem>
            </SelectContent>
          </Select>
        </CardHeader>
        <CardContent>
          {usageData.length > 0 ? (
            <div className="h-[250px]">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={usageData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
                  <XAxis 
                    dataKey="date" 
                    tickFormatter={(value) => new Date(value).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                    stroke="#6B7280"
                    fontSize={12}
                  />
                  <YAxis yAxisId="left" stroke="#6B7280" fontSize={12} />
                  <YAxis yAxisId="right" orientation="right" stroke="#6B7280" fontSize={12} />
                  <Tooltip 
                    contentStyle={TOOLTIP_STYLE}
                    formatter={(value, name) => [
                      name === 'usage_count' ? `${value} items` : `$${value.toFixed(2)}`,
                      name === 'usage_count' ? 'Items Used' : 'Value'
                    ]}
                  />
                  <Legend />
                  <Bar 
                    yAxisId="left"
                    dataKey="usage_count" 
                    name="Items Used"
                    fill="#0776BD" 
                    radius={[4, 4, 0, 0]}
                  />
                  <Line 
                    yAxisId="right"
                    type="monotone" 
                    dataKey="total_value" 
                    name="Value ($)"
                    stroke="#F0C500" 
                    strokeWidth={2}
                    dot={DOT_STYLE}
                  />
                </BarChart>
              </ResponsiveContainer>
            </div>
          ) : (
            <div className="h-[250px] flex items-center justify-center text-[#6B7280]">
              No usage data available for the selected period
            </div>
          )}
        </CardContent>
      </Card>

      {/* Filters */}
      <Card className="bg-white rounded-xl border border-gray-200 shadow-soft overflow-hidden">
        <CardHeader className="flex flex-row items-center justify-between pb-2">
          <CardTitle className="font-heading text-lg text-[#1F2937] flex items-center gap-2">
            <Filter size={20} className="text-[#0776BD]" />
            Transaction Filters
          </CardTitle>
          <Button 
            variant="ghost" 
            onClick={() => setShowFilters(!showFilters)}
            className="text-[#0776BD]"
          >
            {showFilters ? 'Hide Filters' : 'Show Filters'}
          </Button>
        </CardHeader>
        {showFilters && (
          <CardContent className="pt-0">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 xl:grid-cols-7 gap-4">
              <div className="space-y-1">
                <Label className="text-xs text-[#6B7280]">Inventory</Label>
                <Select value={selectedInventory} onValueChange={setSelectedInventory}>
                  <SelectTrigger className="h-10 rounded-lg border-gray-300">
                    <SelectValue placeholder="All" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Inventories</SelectItem>
                    {inventories.map(inv => (
                      <SelectItem key={inv.id} value={inv.id}>{inv.inventory_name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-1">
                <Label className="text-xs text-[#6B7280]">Item</Label>
                <Select value={selectedItem} onValueChange={setSelectedItem}>
                  <SelectTrigger className="h-10 rounded-lg border-gray-300">
                    <SelectValue placeholder="All" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Items</SelectItem>
                    {items.map(item => (
                      <SelectItem key={item.id} value={item.id}>{item.item_name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-1">
                <Label className="text-xs text-[#6B7280]">Category</Label>
                <Select value={selectedCategory} onValueChange={setSelectedCategory}>
                  <SelectTrigger className="h-10 rounded-lg border-gray-300">
                    <SelectValue placeholder="All" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Categories</SelectItem>
                    {categories.map(cat => (
                      <SelectItem key={cat.id} value={cat.id}>{cat.category_name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-1">
                <Label className="text-xs text-[#6B7280]">Vendor</Label>
                <Select value={selectedVendor} onValueChange={setSelectedVendor}>
                  <SelectTrigger className="h-10 rounded-lg border-gray-300">
                    <SelectValue placeholder="All" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Vendors</SelectItem>
                    {vendors.map(vendor => (
                      <SelectItem key={vendor.id} value={vendor.id}>{vendor.vendor_name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-1">
                <Label className="text-xs text-[#6B7280]">Type</Label>
                <Select value={selectedType} onValueChange={setSelectedType}>
                  <SelectTrigger className="h-10 rounded-lg border-gray-300">
                    <SelectValue placeholder="All" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Types</SelectItem>
                    <SelectItem value="use">Use</SelectItem>
                    <SelectItem value="restock">Restock</SelectItem>
                    <SelectItem value="transfer_in">Transfer In</SelectItem>
                    <SelectItem value="transfer_out">Transfer Out</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-1">
                <Label className="text-xs text-[#6B7280]">Start Date</Label>
                <Input 
                  type="date" 
                  value={startDate} 
                  onChange={(e) => setStartDate(e.target.value)}
                  className="h-10 rounded-lg border-gray-300 text-sm"
                />
              </div>
              <div className="space-y-1">
                <Label className="text-xs text-[#6B7280]">End Date</Label>
                <Input 
                  type="date" 
                  value={endDate} 
                  onChange={(e) => setEndDate(e.target.value)}
                  className="h-10 rounded-lg border-gray-300 text-sm"
                />
              </div>
            </div>
          </CardContent>
        )}
      </Card>

      {/* Transaction History */}
      <Card className="bg-white rounded-xl border border-gray-200 shadow-soft">
        <CardHeader>
          <CardTitle className="font-heading text-lg text-[#1F2937]">Transaction History</CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-[#0776BD]" />
            </div>
          ) : transactions.length > 0 ? (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="bg-[#F5F6F7]">
                    <TableHead 
                      className="font-semibold text-[#1F2937] cursor-pointer hover:bg-gray-200"
                      onClick={() => handleSort('timestamp')}
                    >
                      <div className="flex items-center gap-1">
                        Date/Time
                        <SortIcon column="timestamp" />
                      </div>
                    </TableHead>
                    <TableHead className="font-semibold text-[#1F2937]">Type</TableHead>
                    <TableHead 
                      className="font-semibold text-[#1F2937] cursor-pointer hover:bg-gray-200"
                      onClick={() => handleSort('item_id')}
                    >
                      <div className="flex items-center gap-1">
                        Item
                        <SortIcon column="item_id" />
                      </div>
                    </TableHead>
                    <TableHead className="font-semibold text-[#1F2937]">Inventory</TableHead>
                    <TableHead 
                      className="font-semibold text-[#1F2937] text-center cursor-pointer hover:bg-gray-200"
                      onClick={() => handleSort('quantity')}
                    >
                      <div className="flex items-center justify-center gap-1">
                        Qty
                        <SortIcon column="quantity" />
                      </div>
                    </TableHead>
                    <TableHead className="font-semibold text-[#1F2937] text-right">Value</TableHead>
                    <TableHead className="font-semibold text-[#1F2937]">Job #</TableHead>
                    <TableHead className="font-semibold text-[#1F2937]">Vendor</TableHead>
                    <TableHead className="font-semibold text-[#1F2937]">By</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {transactions.map((trans) => (
                    <TableRow key={trans.id} className="hover:bg-[#F5F6F7]" data-testid={`transaction-${trans.id}`}>
                      <TableCell className="text-sm text-[#6B7280]">
                        <div>
                          {new Date(trans.timestamp).toLocaleDateString()}
                        </div>
                        <div className="text-xs">
                          {new Date(trans.timestamp).toLocaleTimeString()}
                        </div>
                      </TableCell>
                      <TableCell>
                        <Badge className={`${getTransactionBadge(trans.transaction_type)} border-0`}>
                          {trans.transaction_type.replace('_', ' ')}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <div className="font-medium text-[#1F2937]">{trans.item_name}</div>
                        <div className="text-xs text-[#0776BD] font-mono">({trans.internal_code})</div>
                      </TableCell>
                      <TableCell className="text-[#6B7280]">{trans.inventory_name}</TableCell>
                      <TableCell className={`text-center font-medium ${trans.quantity < 0 ? 'text-red-600' : 'text-green-600'}`}>
                        {trans.quantity > 0 ? '+' : ''}{trans.quantity}
                      </TableCell>
                      <TableCell className="text-right text-[#1F2937]">${Math.abs(trans.total_value).toFixed(2)}</TableCell>
                      <TableCell className="text-[#6B7280]">{trans.job_number || '-'}</TableCell>
                      <TableCell className="text-[#6B7280]">{trans.vendor_name || '-'}</TableCell>
                      <TableCell className="text-[#6B7280] text-sm">{trans.performed_by_name}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          ) : (
            <div className="py-12 text-center text-[#6B7280]">
              No transactions found with current filters
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default Reports;
